import type { ComponentId, XyzDataPoint } from '../types';

/**
 * One component's raw field values, before any coercion.
 *
 * Deliberately `unknown` rather than `number`: these come off `getComponentFieldValue` and
 * `getFieldValueForComponentOrReference`, which return whatever is stored. A component with
 * no value for the chosen field, a field that was deleted, and a calculated field that
 * produced `NaN` all arrive here and all have to be told apart from a real zero.
 */
export type RawComponentValues = {
  componentId: ComponentId;
  label: string;
  x: unknown;
  y: unknown;
  z: unknown;
  radius: unknown;
  colorValue: unknown;
  fill: string | null;
  isContext: boolean;
};

/** The coordinates a component must have to be plottable at all. */
export type RequiredDimension = 'x' | 'y' | 'z';

export type XyzDataPointsResult = {
  points: XyzDataPoint[];
  /**
   * How many components were left out, and which missing coordinate was to blame.
   *
   * Surfaced rather than counted silently: "412 of 460 components plotted" is the difference
   * between a chart a viewer can trust and one that quietly represents a subset. A component
   * can be missing more than one coordinate; it is attributed to the first in x, y, z order.
   */
  excludedByDimension: Record<RequiredDimension, number>;
  excludedCount: number;
};

export type XyzDataPointsArgs = {
  values: RawComponentValues[];
};

/**
 * Accepts only real, finite numbers.
 *
 * `Number.isFinite` rather than a `typeof` check alone: a calculated field can yield `NaN` or
 * `Infinity`, and either poisons every downstream range, scale and colour lookup with a
 * single component. Numeric strings are rejected too - Ardoq number fields hold numbers, and
 * silently parsing `"12abc"` into 12 invents data.
 */
const toFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Turns raw field values into plottable points, dropping components that cannot be placed.
 *
 * Excluding rather than defaulting is the substantive difference from the bubble chart, which
 * coerces a missing x or y to 0 (`safeViewNodeDimensions`). On a bubble chart that produces a
 * visible cluster at the origin, which a viewer can at least recognise as suspicious. On a
 * surface or contour it is worse than misleading: every component missing a coordinate lands
 * on the single position (0, 0), becomes one collapsed sample there, and anchors the
 * interpolation - so one corner of the surface is pulled to the average z of everything the
 * chart could not actually place.
 */
export const toXyzDataPoints = ({
  values,
}: XyzDataPointsArgs): XyzDataPointsResult => {
  const points: XyzDataPoint[] = [];
  const excludedByDimension: Record<RequiredDimension, number> = {
    x: 0,
    y: 0,
    z: 0,
  };

  for (const raw of values) {
    const x = toFiniteNumber(raw.x);
    const y = toFiniteNumber(raw.y);
    const z = toFiniteNumber(raw.z);

    if (x === null) {
      excludedByDimension.x++;
      continue;
    }
    if (y === null) {
      excludedByDimension.y++;
      continue;
    }
    if (z === null) {
      excludedByDimension.z++;
      continue;
    }

    points.push({
      componentId: raw.componentId,
      /* The component id doubles as the unique id: one component plots once per chart, unlike
         the reference variant of the bubble chart where a component can appear several times. */
      uniqueId: raw.componentId,
      x,
      y,
      z,
      /* Optional dimensions stay null so a renderer can tell "no size field chosen" from a
         size of zero, and fall back to the ramp rather than to a default radius. */
      radius: toFiniteNumber(raw.radius),
      colorValue: toFiniteNumber(raw.colorValue),
      label: raw.label,
      fill: raw.fill,
      isContext: raw.isContext,
    });
  }

  return {
    points,
    excludedByDimension,
    excludedCount:
      excludedByDimension.x + excludedByDimension.y + excludedByDimension.z,
  };
};
